import {
  CalendarToday as CalendarIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  LocalShipping as LocalShippingIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CargoCompanyDetail } from "../../../types/cargoCompany";

interface CargoCompanyDetailModalProps {
  open: boolean;
  company: CargoCompanyDetail | null;
  isLoading?: boolean;
  onClose: () => void;
  onEdit?: (company: CargoCompanyDetail) => void;
}

const CargoCompanyDetailModal: React.FC<CargoCompanyDetailModalProps> = ({
  open,
  company,
  isLoading = false,
  onClose,
  onEdit,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!company && !isLoading) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <LocalShippingIcon sx={{ fontSize: 32, color: "primary.main" }} />
            <Box>
              <Typography variant="h6">
                {company?.name || t("admin.cargoCompanies.loading")}
              </Typography>
              {company && (
                <Typography variant="body2" color="text.secondary">
                  {t("admin.cargoCompanies.code")}: {company.code}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : company ? (
          <Box>
            <Grid container spacing={3}>
              {/* Status */}
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Chip
                    label={company.isActive ? t("admin.cargoCompanies.active") : t("admin.cargoCompanies.inactive")}
                    color={company.isActive ? "success" : "default"}
                    icon={
                      company.isActive ? <CheckCircleIcon /> : <CancelIcon />
                    }
                  />
                </Box>
              </Grid>

              {/* Contact Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {t("admin.cargoCompanies.contactInformation")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List>
                  <ListItem>
                    <EmailIcon sx={{ mr: 2, color: "text.secondary" }} />
                    <ListItemText
                      primary={t("admin.cargoCompanies.email")}
                      secondary={company.contactEmail}
                    />
                  </ListItem>
                  {company.contactPhone && (
                    <ListItem>
                      <PhoneIcon sx={{ mr: 2, color: "text.secondary" }} />
                      <ListItemText
                        primary={t("admin.cargoCompanies.phone")}
                        secondary={company.contactPhone}
                      />
                    </ListItem>
                  )}
                  {company.address && (
                    <ListItem>
                      <LocationIcon sx={{ mr: 2, color: "text.secondary" }} />
                      <ListItemText
                        primary={t("admin.cargoCompanies.address")}
                        secondary={company.address}
                      />
                    </ListItem>
                  )}
                </List>
              </Grid>

              {/* Statistics */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {t("admin.cargoCompanies.statistics")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                      <LocalShippingIcon
                        sx={{ fontSize: 32, color: "primary.main", mb: 1 }}
                      />
                      <Typography variant="h6">
                        {company.totalOrders || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("admin.cargoCompanies.totalOrders")}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                      <PeopleIcon
                        sx={{ fontSize: 32, color: "primary.main", mb: 1 }}
                      />
                      <Typography variant="h6">
                        {company.activeUsers || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("admin.cargoCompanies.activeUsers")}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>

              {/* Assigned Users */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6" gutterBottom>
                    {t("admin.cargoCompanies.assignedUsersCount", { count: company.users?.length || 0 })}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PeopleIcon />}
                    onClick={() => {
                      navigate(`/admin/cargo-companies/${company.id}/users`);
                      onClose();
                    }}
                  >
                    {t("admin.cargoCompanies.manageUsers")}
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {company.users && company.users.length > 0 ? (
                  <List>
                    {company.users.slice(0, 5).map((user) => (
                      <ListItem key={user.userId}>
                        <ListItemText
                          primary={`${user.userFirstName} ${user.userLastName}`}
                          secondary={
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {user.userEmail}
                              </Typography>
                              <Chip
                                label={user.isActive ? t("admin.cargoCompanies.active") : t("admin.cargoCompanies.inactive")}
                                size="small"
                                color={user.isActive ? "success" : "default"}
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                    {company.users.length > 5 && (
                      <ListItem>
                        <Typography variant="body2" color="text.secondary">
                          {t("admin.cargoCompanies.andMoreUsers", { count: company.users.length - 5 })}
                        </Typography>
                      </ListItem>
                    )}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.cargoCompanies.noUsersAssigned")}
                  </Typography>
                )}
              </Grid>

              {/* Metadata */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {t("admin.cargoCompanies.metadata")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List>
                  <ListItem>
                    <CalendarIcon sx={{ mr: 2, color: "text.secondary" }} />
                    <ListItemText
                      primary={t("admin.cargoCompanies.createdAt")}
                      secondary={formatDate(company.createdAt)}
                    />
                  </ListItem>
                  {company.updatedAt && (
                    <ListItem>
                      <CalendarIcon sx={{ mr: 2, color: "text.secondary" }} />
                      <ListItemText
                        primary={t("admin.cargoCompanies.updatedAt")}
                        secondary={formatDate(company.updatedAt)}
                      />
                    </ListItem>
                  )}
                </List>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Alert severity="error">{t("admin.cargoCompanies.failedToLoadDetails")}</Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t("admin.cargoCompanies.close")}</Button>
        {company && onEdit && (
          <Button onClick={() => onEdit(company)} variant="contained">
            {t("admin.cargoCompanies.edit")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CargoCompanyDetailModal;
